import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "../contexts/AuthContext";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const formatCurrency = (v) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(v);

export default function TenantsPage() {
  const [tenants, setTenants] = useState([]);
  const [spots, setSpots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", nif: "", address: "", parking_spot_id: "", monthly_price: "", active: true });

  const load = useCallback(async () => {
    try {
      const [t, s] = await Promise.all([
        axios.get(`${API}/tenants`),
        axios.get(`${API}/parking-spots`),
      ]);
      setTenants(t.data);
      setSpots(s.data);
    } catch (e) { if (e.response?.status !== 401) toast.error("Error cargando datos"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm({ name: "", nif: "", address: "", parking_spot_id: "", monthly_price: "", active: true });
    setDialogOpen(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({ name: t.name, nif: t.nif, address: t.address, parking_spot_id: t.parking_spot_id, monthly_price: String(t.monthly_price), active: t.active });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const payload = { ...form, monthly_price: parseFloat(form.monthly_price) };
    try {
      if (editing) {
        await axios.put(`${API}/tenants/${editing.id}`, payload);
        toast.success("Inquilino actualizado");
      } else {
        await axios.post(`${API}/tenants`, payload);
        toast.success("Inquilino creado");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error guardando");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este inquilino?")) return;
    try {
      await axios.delete(`${API}/tenants/${id}`);
      toast.success("Inquilino eliminado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error eliminando");
    }
  };

  const getSpotName = (id) => spots.find(s => s.id === id)?.identifier || "—";

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Cargando...</div>;

  return (
    <div data-testid="tenants-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl sm:text-3xl tracking-tight font-bold text-gray-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Inquilinos</h2>
          <p className="text-sm text-gray-500 mt-1">{tenants.length} inquilinos registrados</p>
        </div>
        <button onClick={openNew} className="bg-[#002FA7] text-white hover:bg-[#00227B] rounded-sm px-4 py-2 text-sm font-semibold transition-all duration-200 flex items-center gap-2" data-testid="add-tenant-button">
          <Plus className="w-4 h-4" /> Nuevo inquilino
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-sm" data-testid="tenants-table">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold">Nombre</TableHead>
              <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold">NIF</TableHead>
              <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold">Direccion</TableHead>
              <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold">Plaza</TableHead>
              <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold text-right">Precio/mes</TableHead>
              <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold">Estado</TableHead>
              <TableHead className="text-xs tracking-[0.15em] uppercase text-gray-500 font-bold text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tenants.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-gray-400">No hay inquilinos</TableCell></TableRow>
            ) : tenants.map((t) => (
              <TableRow key={t.id} className="hover:bg-gray-50 transition-colors">
                <TableCell className="text-sm font-medium text-gray-900 py-4 px-4">{t.name}</TableCell>
                <TableCell className="text-sm text-gray-600 py-4 px-4">{t.nif}</TableCell>
                <TableCell className="text-sm text-gray-600 py-4 px-4 max-w-[200px] truncate">{t.address}</TableCell>
                <TableCell className="text-sm text-gray-900 py-4 px-4">{getSpotName(t.parking_spot_id)}</TableCell>
                <TableCell className="text-sm font-medium text-gray-900 py-4 px-4 text-right">{formatCurrency(t.monthly_price)}</TableCell>
                <TableCell className="py-4 px-4">
                  <span className={`status-badge ${t.active ? "status-pagada" : "status-pendiente"}`}>{t.active ? "Activo" : "Inactivo"}</span>
                </TableCell>
                <TableCell className="py-4 px-4 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => openEdit(t)} className="p-1.5 text-gray-400 hover:text-[#002FA7] transition-colors" data-testid={`edit-tenant-${t.id}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(t.id)} className="p-1.5 text-gray-400 hover:text-red-600 transition-colors" data-testid={`delete-tenant-${t.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-sm" data-testid="tenant-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar inquilino" : "Nuevo inquilino"}</DialogTitle>
            <DialogDescription>{editing ? "Modifica los datos del inquilino" : "Introduce los datos del nuevo inquilino"}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm font-semibold text-gray-900 mb-1 block">Nombre</Label>
              <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Nombre completo" className="rounded-sm" data-testid="tenant-name-input" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-gray-900 mb-1 block">NIF</Label>
              <Input value={form.nif} onChange={e => setForm({...form, nif: e.target.value})} placeholder="12345678A" className="rounded-sm" data-testid="tenant-nif-input" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-gray-900 mb-1 block">Direccion</Label>
              <Input value={form.address} onChange={e => setForm({...form, address: e.target.value})} placeholder="Calle, Ciudad, CP" className="rounded-sm" data-testid="tenant-address-input" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-gray-900 mb-1 block">Plaza asignada</Label>
              <Select value={form.parking_spot_id} onValueChange={v => setForm({...form, parking_spot_id: v})}>
                <SelectTrigger className="rounded-sm" data-testid="tenant-spot-select">
                  <SelectValue placeholder="Selecciona plaza" />
                </SelectTrigger>
                <SelectContent>
                  {spots.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.identifier} - {s.address}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-semibold text-gray-900 mb-1 block">Precio mensual (sin IVA)</Label>
              <Input type="number" step="0.01" value={form.monthly_price} onChange={e => setForm({...form, monthly_price: e.target.value})} placeholder="200.00" className="rounded-sm" data-testid="tenant-price-input" />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setDialogOpen(false)} className="bg-white text-gray-900 border border-gray-200 hover:bg-gray-50 rounded-sm px-4 py-2 text-sm font-semibold transition-all" data-testid="tenant-cancel-button">Cancelar</button>
            <button onClick={handleSave} className="bg-[#002FA7] text-white hover:bg-[#00227B] rounded-sm px-4 py-2 text-sm font-semibold transition-all" data-testid="tenant-save-button">Guardar</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
